import { useState, useEffect } from 'react'

interface DaySchedule {
  enabled: boolean
  open: string
  close: string
}

interface BusinessHours {
  monday: DaySchedule
  tuesday: DaySchedule
  wednesday: DaySchedule
  thursday: DaySchedule
  friday: DaySchedule
  saturday: DaySchedule
  sunday: DaySchedule
}

interface BusinessHoursEditorProps {
  initialHours: BusinessHours | null
  connectionId: number
  timezone: string
  onUpdate: (hours: BusinessHours) => void
}

const DAYS = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' },
] as const

const DEFAULT_HOURS: BusinessHours = {
  monday: { enabled: true, open: '09:00', close: '18:00' },
  tuesday: { enabled: true, open: '09:00', close: '18:00' },
  wednesday: { enabled: true, open: '09:00', close: '18:00' },
  thursday: { enabled: true, open: '09:00', close: '18:00' },
  friday: { enabled: true, open: '09:00', close: '18:00' },
  saturday: { enabled: false, open: '09:00', close: '18:00' },
  sunday: { enabled: false, open: '09:00', close: '18:00' },
}

export default function BusinessHoursEditor({ 
  initialHours,
  connectionId,
  timezone,
  onUpdate 
}: BusinessHoursEditorProps) {
  const [hours, setHours] = useState<BusinessHours>(initialHours || DEFAULT_HOURS)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (initialHours) {
      setHours(initialHours)
    }
  }, [initialHours])

  const handleDayToggle = (day: keyof BusinessHours) => {
    setHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled
      }
    }))
    setHasChanges(true)
    setSaved(false)
  }

  const handleTimeChange = (day: keyof BusinessHours, field: 'open' | 'close', value: string) => {
    setHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }))
    setHasChanges(true)
    setSaved(false)
  }

  const handleCopyToAll = (day: keyof BusinessHours) => {
    const template = hours[day]
    const newHours = { ...hours }
    
    DAYS.forEach(({ key }) => {
      newHours[key] = {
        enabled: template.enabled,
        open: template.open,
        close: template.close
      }
    })
    
    setHours(newHours)
    setHasChanges(true)
    setSaved(false)
  }

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    
    try {
      await onUpdate(hours)
      setSaved(true)
      setHasChanges(false)
      
      setTimeout(() => setSaved(false), 3000)
    } catch (error) {
      console.error('Failed to save business hours:', error)
    } finally {
      setSaving(false)
    }
  }

  // Calculer le statut actuel
  const getCurrentStatus = () => {
    const now = new Date()
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const currentDay = dayNames[now.getDay()] as keyof BusinessHours
    const currentTime = now.toTimeString().slice(0, 5) // HH:MM
    
    const todaySchedule = hours[currentDay]
    
    if (!todaySchedule.enabled) {
      return { isOpen: false, text: 'Fermé aujourd\'hui' }
    }
    
    if (currentTime >= todaySchedule.open && currentTime < todaySchedule.close) {
      return { isOpen: true, text: `Ouvert jusqu'à ${todaySchedule.close}` }
    }
    
    return { isOpen: false, text: `Fermé • Ouvre à ${todaySchedule.open}` }
  }

  const status = getCurrentStatus()

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Horaires d'ouverture
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Fuseau horaire: {timezone}
          </p>
        </div>
        {saved && (
          <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Sauvegardé
          </span>
        )}
      </div>

      {/* Statut actuel */}
      <div className={`rounded-lg border p-3 ${
        status.isOpen 
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-900' 
          : 'bg-gray-50 dark:bg-zinc-800 border-gray-200 dark:border-zinc-700'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${status.isOpen ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
          <span className={`text-sm font-medium ${
            status.isOpen 
              ? 'text-green-900 dark:text-green-100' 
              : 'text-gray-900 dark:text-white'
          }`}>
            {status.text}
          </span>
        </div>
      </div>

      {/* Liste des jours */}
      <div className="space-y-2">
        {DAYS.map(({ key, label }) => (
          <div key={key} className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-3">
            <div className="flex items-center gap-3">
              {/* Toggle */}
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={hours[key].enabled}
                  onChange={() => handleDayToggle(key)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-500 dark:peer-focus:ring-green-600 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-green-600"></div>
              </label>

              {/* Label jour */}
              <div className="flex-1 min-w-0">
                <span className={`text-sm font-medium ${
                  hours[key].enabled 
                    ? 'text-gray-900 dark:text-white' 
                    : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {label}
                </span>
              </div>

              {/* Horaires */}
              {hours[key].enabled ? (
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={hours[key].open}
                    onChange={(e) => handleTimeChange(key, 'open', e.target.value)}
                    className="px-2 py-1 text-sm border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <span className="text-gray-400 dark:text-gray-500">-</span>
                  <input
                    type="time"
                    value={hours[key].close}
                    onChange={(e) => handleTimeChange(key, 'close', e.target.value)}
                    className="px-2 py-1 text-sm border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  
                  {/* Bouton copier */}
                  <button
                    type="button"
                    onClick={() => handleCopyToAll(key)}
                    title="Copier à tous les jours"
                    className="p-1 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              ) : (
                <span className="text-sm text-gray-400 dark:text-gray-500">Fermé</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Info */}
      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
        <div className="flex items-start gap-2">
          <span>💬</span>
          <span>Hors des horaires, l'agent enverra le <strong>message de fermeture</strong></span>
        </div>
        <div className="flex items-start gap-2">
          <span>🤖</span>
          <span>Pendant les horaires, l'agent répond normalement avec le <strong>délai configuré</strong></span>
        </div>
      </div>

      {/* Bouton Sauvegarder */}
      <button
        type="button"
        onClick={handleSave}
        disabled={saving || !hasChanges}
        className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {saving ? (
          <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Sauvegarde...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Sauvegarder les horaires
          </>
        )}
      </button>
    </div>
  )
}
